import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { BookOpen } from 'lucide-react';

type BookIdea = {
  id: string;
  title: string;
  accepting_beta_readers: boolean;
  created_at: string;
  identity_username: string;
};

interface BookIdeaManagerProps {
  user: User;
}

export default function BookIdeaManager({ user }: BookIdeaManagerProps) {
  const [bookIdeas, setBookIdeas] = useState<BookIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [user.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: ideasData, error: ideasError } = await supabase
        .from('book_ideas')
        .select(`
          id, 
          title, 
          accepting_beta_readers,
          created_at,
          identity_username
        `)
        .eq('author_id', user.id);

      if (ideasError) throw ideasError;
      setBookIdeas(ideasData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleBetaReaders = async (ideaId: string, currentStatus: boolean) => {
    try {
      setUpdatingId(ideaId);
      setError(null);

      const { error: updateError } = await supabase
        .from('book_ideas')
        .update({ accepting_beta_readers: !currentStatus })
        .eq('id', ideaId);

      if (updateError) throw updateError;

      setBookIdeas(prev =>
        prev.map(idea =>
          idea.id === ideaId
            ? { ...idea, accepting_beta_readers: !currentStatus }
            : idea
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update beta reader status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-violet-500" />
        Your Book Ideas
      </h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      
      <div className="divide-y">
        {bookIdeas.map(idea => (
          <div key={idea.id} className="py-4 first:pt-0 last:pb-0">
            <div>
              <h3 className="font-medium text-gray-900">{idea.title}</h3>
              <p className="text-sm text-gray-500 mb-2">
                Posted as {idea.identity_username} • Created {new Date(idea.created_at).toLocaleDateString()}
              </p>
              <button
                onClick={() => toggleBetaReaders(idea.id, idea.accepting_beta_readers)}
                disabled={updatingId === idea.id}
                className={`
                  w-full text-left px-4 py-2 rounded-lg transition-colors
                  ${idea.accepting_beta_readers
                    ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                  ${updatingId === idea.id ? 'opacity-50 cursor-wait' : ''}
                `}
              >
                {idea.accepting_beta_readers ? 'Accepting Beta Readers' : 'Closed to Beta Readers'}
              </button>
            </div>
          </div>
        ))}

        {bookIdeas.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No book ideas found</p>
            <p className="text-sm mt-1">Create a book idea to start accepting beta readers</p>
          </div>
        )}
      </div>
    </div>
  );
}